import Logger from "../../clients/logger";

/**
 * On insecure sites (e.g., http://mywebsite.com), window.crypto.subtle does not exist.
 * Any encryption/decryption will fail on these sites.
 * This is a helper to see if we can use those methods.
 */
const cryptoExists = () => window.crypto.subtle !== undefined;

const ENCRYPTION_DISALLOWED_MESSAGE =
	"Encryption is unsupported on insecure websites. Please upgrade to https to use encryption.";

export default class MyCrypto {
	private myIv?: Uint8Array;

	private myKey?: CryptoKey;

	constructor(inputSimpleKey: string, ready: Function) {
		if (!cryptoExists()) {
			Logger.system.log("APPLICATION LIFECYCLE:STARTUP:AUTHENTICATION", ENCRYPTION_DISALLOWED_MESSAGE);
			return ready();
		}

		this.myIv = new window.TextEncoder().encode("w5ZK8yaOfktXmgny"); // 16-byte initialization vector used for encrypt and decrypt

		window.crypto.subtle
			.importKey(
				"jwk",
				{
					// the JSON Web Key (jwk)
					kty: "oct",
					k: this.getSeedKey(inputSimpleKey),
					alg: "A256CBC",
					ext: true,
				},
				{ name: "AES-CBC" },
				false, // whether the key can be used in exportKey
				["encrypt", "decrypt"] // can be "encrypt", "decrypt", "wrapKey", or "unwrapKey"
			)
			.then((key) => {
				this.myKey = key; // the true key -- a symmetric key
				Logger.system.debug(
					"APPLICATION LIFECYCLE:STARTUP:importKey success",
					inputSimpleKey,
					this.getSeedKey(inputSimpleKey),
					"K",
					key,
					"M",
					this.myKey
				);
				ready();
			})
			.catch((err) => {
				Logger.system.error("APPLICATION LIFECYCLE:STARTUP:importKey failure", err);
				// band-aid. Crypto doesn't work on insecure sites.
				ready();
			});
	}

	getSeedKey(simpleKey: string): string {
		const index = 4; // where to substitute simple key into base key
		const baseKey = "t0zt37HgOxMBY7SZjYVmrqhPkO44Ii2Jcb9yedUEPfE"; // random key of required size that will be overlaid with simple key (to get a better result)
		const truncatedSimpleKey = simpleKey.substring(0, baseKey.length - (index + 1)); // simple key can't be too long to fit into base

		// the seed key used to import/create the true key used for encrypt and decrypt
		return baseKey.substr(0, index) + truncatedSimpleKey + baseKey.substr(index + truncatedSimpleKey.length); // substitute into base
	}

	arrayBufferToString(buf: ArrayBuffer): string {
		const buffer: unknown = new Uint16Array(buf);
		return String.fromCharCode.apply(null, buffer as number[]);
	}

	stringToArrayBuffer(str: string): ArrayBuffer {
		const buf = new ArrayBuffer(str.length * 2); // 2 bytes for each char
		const bufView = new Uint16Array(buf);
		for (let i = 0, strLen = str.length; i < strLen; i++) {
			bufView[i] = str.charCodeAt(i);
		}
		return buf;
	}

	encryptString(stringToEncrypt: string, callback: Function): void {
		// See cryptoExists for an explanation.
		if (!cryptoExists()) {
			// deliberately do not call the callback.
			// Prior to 4.0.0 these methods would just throw uncaught execeptions and fail.
			// Now they log errors and fail.
			return Logger.system.error(ENCRYPTION_DISALLOWED_MESSAGE);
		}

		if (!this.myKey) {
			return Logger.system.error("Unable to encrypt string. Crypto key missing.");
		}

		const utfData = new window.TextEncoder().encode(stringToEncrypt);
		window.crypto.subtle
			.encrypt(
				{
					name: "AES-CBC",
					iv: this.myIv, // should eventually create new iv for each encryption
				},
				this.myKey,
				utfData // ArrayBuffer of data to encrypt
			)
			.then((encryptedData) => {
				const encryptedString = this.arrayBufferToString(encryptedData);
				Logger.system.debug("encryptString", "encryptedData", encryptedData, encryptedString);
				// returns an ArrayBuffer containing the encrypted data
				callback(encryptedString);
			})
			.catch((err) => {
				Logger.system.error(err);
			});
	}

	decryptString(encryptedString: string, callback: Function): void {
		const encryptedData = this.stringToArrayBuffer(encryptedString);

		// See cryptoExists for an explanation.
		if (!cryptoExists()) {
			// deliberately do not call the callback.
			// Prior to 4.0.0 these methods would just throw uncaught execeptions and fail.
			// Now they log errors and fail.
			return Logger.system.error(ENCRYPTION_DISALLOWED_MESSAGE);
		}

		if (!this.myKey) {
			return Logger.system.error("Unable to decrypt string. Crypto key missing.");
		}

		Logger.system.debug("APPLICATION LIFECYCLE:STARTUP:decryptString", "encryptedData", encryptedData, encryptedString);
		window.crypto.subtle
			.decrypt(
				{
					name: "AES-CBC",
					iv: this.myIv,
				},
				this.myKey,
				encryptedData
			)
			.then((decrypted) => {
				const decryptedString = new TextDecoder("utf-8").decode(new Uint8Array(decrypted));
				callback(decryptedString);
			})
			.catch((err) => {
				Logger.system.error(
					"decryptString error",
					err,
					"myKey",
					this.myKey,
					"encryptedData",
					encryptedData,
					"encryptedString",
					encryptedString
				);
			});
	}
}
